import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, X, Image, Video, Eye, MapPin,
  ShieldCheck, Building2, Scale, IndianRupee, Search,
  Compass, Home, Upload, Lock, Loader2, Trash2, Info, Users
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import LocationPicker from '../../components/common/LocationPicker';
import { formatError } from '../../utils/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== CONSTANTS ====================

const propertyTypes = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'independent_house', label: 'Independent House' },
  { value: 'land', label: 'Land' },
  { value: 'plot', label: 'Plot' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'project', label: 'Project' },
  { value: 'venture', label: 'Venture / Layout' }
];

const projectStatuses = [
  { value: 'ready', label: 'Ready to Move' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'upcoming', label: 'Upcoming' }
];

const sizeUnits = [
  { value: 'sqft', label: 'Sq. Ft.' },
  { value: 'sqyd', label: 'Sq. Yd.' },
  { value: 'sqm', label: 'Sq. M.' },
  { value: 'acres', label: 'Acres' },
  { value: 'guntha', label: 'Guntha' },
  { value: 'cents', label: 'Cents' }
];

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal', 'Delhi', 'Chandigarh', 'Puducherry', 'Ladakh',
  'Jammu and Kashmir', 'Andaman and Nicobar', 'Dadra and Nagar Haveli',
  'Lakshadweep'
];

const facingDirections = [
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'north_east', label: 'North-East' },
  { value: 'north_west', label: 'North-West' },
  { value: 'south_east', label: 'South-East' },
  { value: 'south_west', label: 'South-West' }
];

const propertyAgeOptions = [
  { value: 'new', label: 'New Construction' },
  { value: '1-5', label: '1–5 Years' },
  { value: '5-10', label: '5–10 Years' },
  { value: '10+', label: '10+ Years' }
];

const furnishingTypes = [
  { value: 'unfurnished', label: 'Unfurnished' },
  { value: 'semi_furnished', label: 'Semi-Furnished' },
  { value: 'fully_furnished', label: 'Fully Furnished' }
];

const parkingTypes = [
  { value: 'none', label: 'None' },
  { value: 'open', label: 'Open' },
  { value: 'covered', label: 'Covered' },
  { value: 'both', label: 'Open + Covered' }
];

const waterSources = [
  { value: 'municipal', label: 'Municipal' },
  { value: 'borewell', label: 'Borewell' },
  { value: 'both', label: 'Both' }
];

const approvalAuthorities = [
  { value: 'rera', label: 'RERA' },
  { value: 'hmda', label: 'HMDA' },
  { value: 'dtcp', label: 'DTCP' },
  { value: 'bda', label: 'BDA' },
  { value: 'bmrda', label: 'BMRDA' },
  { value: 'municipal', label: 'Municipal Corporation' },
  { value: 'panchayat', label: 'Gram Panchayat' },
  { value: 'other', label: 'Other' }
];

const landTypes = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'agricultural', label: 'Agricultural' },
  { value: 'industrial', label: 'Industrial' }
];

const ownershipTypes = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'cooperative', label: 'Co-operative Society' },
  { value: 'power_of_attorney', label: 'Power of Attorney' }
];

const defaultAmenities = [
  'Swimming Pool', 'Gym', 'Parking', 'Security', 'Power Backup',
  'Lift', 'Club House', 'Garden', 'Children Play Area', 'Intercom',
  'Rainwater Harvesting', 'Jogging Track', 'Indoor Games', 'CCTV',
  'Fire Safety', 'Vastu Compliant', 'Pet Friendly', 'Senior Citizen Area',
  'Multipurpose Hall', 'Badminton Court'
];

const nearbyFacilityOptions = [
  'School', 'Hospital', 'Metro', 'Bus Stop', 'Railway Station',
  'Mall', 'Supermarket', 'Highway', 'Airport', 'Bank',
  'ATM', 'Pharmacy', 'Park', 'Temple', 'Church', 'Mosque'
];

const propertyDocumentsMap = {
  land: [
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Encumbrance Certificate (EC)', mandatory: true },
    { name: 'Sale Deed / Agreement', mandatory: true },
    { name: 'Property Tax Receipts', mandatory: true },
    { name: 'Patta / Passbook', mandatory: true },
    { name: 'Layout Approval (HMDA/DTCP)', mandatory: true },
    { name: 'Land Conversion Certificate (NA)', mandatory: true },
    { name: 'Possession Certificate', mandatory: false },
    { name: 'LRS Certificate', mandatory: false },
    { name: 'PAN/Aadhaar of Seller', mandatory: true },
  ],
  plot: [
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Encumbrance Certificate (EC)', mandatory: true },
    { name: 'Sale Deed / Agreement', mandatory: true },
    { name: 'Property Tax Receipts', mandatory: true },
    { name: 'Layout Approval (HMDA/DTCP)', mandatory: true },
    { name: 'PAN/Aadhaar of Seller', mandatory: true },
  ],
  apartment: [
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Encumbrance Certificate (EC)', mandatory: true },
    { name: 'Sale Deed / Agreement', mandatory: true },
    { name: 'Property Tax Receipts', mandatory: true },
    { name: 'Khata Certificate / Extract', mandatory: true },
    { name: 'Building Plan Approval', mandatory: true },
    { name: 'Completion Certificate', mandatory: true },
    { name: 'Occupancy Certificate', mandatory: true },
    { name: 'PAN/Aadhaar of Seller', mandatory: true },
  ],
  villa: [
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Encumbrance Certificate (EC)', mandatory: true },
    { name: 'Sale Deed / Agreement', mandatory: true },
    { name: 'Property Tax Receipts', mandatory: true },
    { name: 'Khata Certificate / Extract', mandatory: true },
    { name: 'Building Plan Approval', mandatory: true },
    { name: 'Completion Certificate', mandatory: true },
    { name: 'Occupancy Certificate', mandatory: true },
    { name: 'PAN/Aadhaar of Seller', mandatory: true },
  ],
  independent_house: [
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Encumbrance Certificate (EC)', mandatory: true },
    { name: 'Sale Deed / Agreement', mandatory: true },
    { name: 'Property Tax Receipts', mandatory: true },
    { name: 'Khata Certificate / Extract', mandatory: true },
    { name: 'Building Plan Approval', mandatory: true },
    { name: 'Completion Certificate', mandatory: true },
    { name: 'Occupancy Certificate', mandatory: true },
    { name: 'PAN/Aadhaar of Seller', mandatory: true },
  ],
  project: [
    { name: 'RERA Certificate', mandatory: true },
    { name: 'Approved Site/Layout Plan', mandatory: true },
    { name: 'Land Ownership Documents', mandatory: true },
    { name: 'Title Deed / Mother Deed', mandatory: true },
    { name: 'Audited Balance Sheets', mandatory: false },
    { name: 'Previous Project Completions', mandatory: false },
  ],
  venture: [
    { name: 'RERA Certificate', mandatory: true },
    { name: 'Approved Site/Layout Plan', mandatory: true },
    { name: 'Land Ownership Documents', mandatory: true },
    { name: 'Layout Approval (HMDA/DTCP)', mandatory: true },
  ]
};

const commonDocuments = [
  'Title Deed / Mother Deed',
  'Encumbrance Certificate (EC)',
  'Sale Deed / Agreement',
  'Property Tax Receipts',
  'Khata Certificate / Extract',
  'Patta / Passbook',
  'PAN/Aadhaar of Seller',
  'Building Plan Approval',
  'Completion Certificate',
  'Occupancy Certificate',
  'RERA Certificate',
  'Approved Site/Layout Plan',
  'Land Ownership Documents',
  'Layout Approval (HMDA/DTCP)',
  'Land Conversion Certificate (NA)',
  'Possession Certificate',
  'LRS Certificate',
  'Audited Balance Sheets',
  'Previous Project Completions',
  'No Objection Certificate (NOC)',
];

// ==================== HELPERS ====================

function formatIndianPrice(num) {
  if (!num || isNaN(num)) return '';
  const n = parseFloat(num);
  if (n >= 10000000) {
    const cr = n / 10000000;
    return `₹${cr % 1 === 0 ? cr : cr.toFixed(2)} Cr`;
  }
  if (n >= 100000) {
    const lakh = n / 100000;
    return `₹${lakh % 1 === 0 ? lakh : lakh.toFixed(2)} Lakh`;
  }
  if (n >= 1000) {
    return `₹${n.toLocaleString('en-IN')}`;
  }
  return `₹${n}`;
}

function generateSlug(title) {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Use shared image URL utility
import { getImageUrl } from '../../utils/imageUtils';

// ==================== INFO TOOLTIP COMPONENT ====================

const SECTION_INFO = {
  basic: 'Provide a clear title and accurate description. This is the first thing buyers see — a compelling title increases enquiry rates by up to 3x.',
  location: 'Accurate location helps buyers find your property on map searches. Add pincode and coordinates for best visibility on Google Maps.',
  pricing: 'Set a competitive price. Adding maintenance and registration charges builds trust and reduces back-and-forth with buyers.',
  details: 'Property specifics like floor, facing, and furnishing help buyers shortlist. Listings with complete details get 40% more enquiries.',
  legal: 'RERA registration and legal documents increase buyer confidence. Verified properties sell faster and at better prices.',
  badges: 'Trust badges appear prominently on listings. Verified properties are shown higher in search results.',
  amenities: 'Highlight nearby facilities and amenities. Buyers often filter by amenities — more details mean better search visibility.',
  media: 'Properties with photos get 5x more views. Upload real, well-lit images. Floor plans help buyers understand the layout.',
  seo: 'SEO fields help your listing rank on Google. A good meta description can improve click-through rates from search results.',
};

function InfoTooltip({ section }) {
  const text = SECTION_INFO[section];
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="ml-2 text-navy-400 hover:text-sky-500 transition-colors">
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-sm">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ==================== REQUIRED LABEL COMPONENT ====================

function RequiredLabel({ htmlFor, children, required = false }) {
  return (
    <Label htmlFor={htmlFor}>
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
  );
}

function OptionalBadge() {
  return (
    <span className="text-[10px] font-normal text-navy-400 bg-navy-100 px-1.5 py-0.5 rounded ml-1.5 uppercase tracking-wider">
      Optional
    </span>
  );
}

// ==================== FILE UPLOAD COMPONENT ====================

function FileUploadZone({ onUpload, uploading, accept = 'image/*', multiple = true, label = 'images' }) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) onUpload(Array.from(e.dataTransfer.files));
  }, [onUpload]);

  const handleFileSelect = (e) => {
    if (e.target.files?.length) {
      onUpload(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !uploading && fileInputRef.current?.click()}
      className={`
        relative flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all
        ${dragActive
          ? 'border-sky-500 bg-sky-50'
          : 'border-navy-200 hover:border-sky-400 hover:bg-navy-50/50'}
        ${uploading ? 'pointer-events-none opacity-60' : ''}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
      />
      {uploading ? (
        <>
          <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
          <p className="text-sm text-navy-600">Uploading...</p>
        </>
      ) : (
        <>
          <Upload className="w-8 h-8 text-navy-400" />
          <div className="text-center">
            <p className="text-sm font-medium text-navy-700">
              Click to upload or drag & drop
            </p>
            <p className="text-xs text-navy-400 mt-1">
              Supports JPG, PNG, WebP, GIF {label}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ==================== IMAGE PREVIEW GRID ====================

function ImagePreviewGrid({ images, onRemove }) {
  if (!images.length) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-3">
      {images.map((url, index) => (
        <div key={`${url}-${index}`} className="group relative aspect-square rounded-lg overflow-hidden bg-navy-100 border border-navy-200">
          <img
            src={getImageUrl(url)}
            alt={`Preview ${index + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = ''; e.target.alt = 'Error'; }}
          />
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] text-center py-0.5">
            {index + 1}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== DEFAULT FORM DATA ====================

const getDefaultFormData = () => ({
  title: '',
  property_type: 'apartment',
  description: '',
  project_status: 'ready',
  location_state: '',
  location_city: '',
  location_area: '',
  location_pincode: '',
  full_address: '',
  latitude: '',
  longitude: '',
  price: '',
  price_unit: 'INR',
  size: '',
  size_unit: 'sqft',
  price_per_sqft: '',
  maintenance_charges: '',
  registration_charges: '',
  bedrooms: '',
  bathrooms: '',
  property_age: '',
  year_built: '',
  floor_number: '',
  total_floors: '',
  facing_direction: '',
  furnishing_type: '',
  parking_type: '',
  power_backup: false,
  water_source: '',
  gated_community: false,
  corner_plot: false,
  rera_number: '',
  approval_authority: '',
  land_type: '',
  ownership_type: '',
  encumbrance_certificate: false,
  bank_approved: false,
  possession_date: '',
  rera_verified: false,
  badge_bank_approved: false,
  badge_legal_checked: false,
  badge_hmda_approved: false,
  badge_dtcp_approved: false,
  images: [],
  video_tour_url: '',
  virtual_tour_url: '',
  floor_plan_images: [],
  meta_title: '',
  meta_description: '',
  slug: '',
  amenities: [],
  nearby_facilities: [],
  nearby_highlights: [],
  featured: false,
  is_premium: false,
  total_units: '',
  launch_date: '',
  completion_date: '',
  plot_facing: '',
  road_width: '',
  layout_approved: false,
  // Venture-specific
  venture_name: '',
  master_land_area: '',
  master_land_area_unit: 'acres',
  layout_map_images: [],
  units: [],
  // Multi-partner support
  partners: [],
  documents: [],
});

// ==================== COMPONENT ====================

export default function PropertyFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { getAuthHeader, agency } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingProperty, setFetchingProperty] = useState(isEdit);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [highlightInput, setHighlightInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFloorPlans, setUploadingFloorPlans] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState({});
  const [customDocName, setCustomDocName] = useState('');

  const isPremium = agency?.subscription === 'premium' || agency?.subscription === 'enterprise';
  const maxImages = isPremium ? 20 : 4;
  const maxFloorPlans = isPremium ? 20 : 4;

  const isApartmentOrVilla = ['apartment', 'villa', 'independent_house'].includes(formData.property_type);
  const isLandOrPlot = ['land', 'plot'].includes(formData.property_type);
  const isProject = formData.property_type === 'project';
  const isVenture = formData.property_type === 'venture';
  const isUnderConstruction = formData.project_status === 'under_construction';

  // Auto-calculate price per sqft
  const pricePerSqft = useMemo(() => {
    const price = parseFloat(formData.price);
    const size = parseFloat(formData.size);
    if (price > 0 && size > 0) return Math.round(price / size);
    return null;
  }, [formData.price, formData.size]);

  const formattedPrice = useMemo(() => formatIndianPrice(formData.price), [formData.price]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEdit && formData.title) {
      updateField('slug', generateSlug(formData.title));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title, isEdit]);

  useEffect(() => {
    if (isEdit) fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties/${id}`, {
        headers: getAuthHeader()
      });
      const prop = response.data;
      setFormData({
        ...getDefaultFormData(),
        ...prop,
        price: prop.price?.toString() || '',
        size: prop.size?.toString() || '',
        bedrooms: prop.bedrooms?.toString() || '',
        bathrooms: prop.bathrooms?.toString() || '',
        floor_number: prop.floor_number?.toString() || '',
        total_floors: prop.total_floors?.toString() || '',
        total_units: prop.total_units?.toString() || '',
        maintenance_charges: prop.maintenance_charges?.toString() || '',
        registration_charges: prop.registration_charges?.toString() || '',
        price_per_sqft: prop.price_per_sqft?.toString() || '',
        year_built: prop.year_built || '',
        amenities: prop.amenities || [],
        images: prop.images || [],
        floor_plan_images: prop.floor_plan_images || [],
        nearby_facilities: prop.nearby_facilities || [],
        nearby_highlights: prop.nearby_highlights || [],
        // Venture fields
        venture_name: prop.venture_name || '',
        master_land_area: prop.master_land_area?.toString() || '',
        master_land_area_unit: prop.master_land_area_unit || 'acres',
        layout_map_images: prop.layout_map_images || [],
        documents: prop.documents || [],
        units: (prop.units || []).map(u => ({
          ...u,
          size: u.size?.toString() || '',
          price: u.price?.toString() || '',
        })),
      });
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/dashboard/properties');
    } finally {
      setFetchingProperty(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const toggleNearbyFacility = (facility) => {
    setFormData(prev => ({
      ...prev,
      nearby_facilities: prev.nearby_facilities.includes(facility)
        ? prev.nearby_facilities.filter(f => f !== facility)
        : [...prev.nearby_facilities, facility]
    }));
  };

  const addHighlight = () => {
    if (highlightInput.trim()) {
      setFormData(prev => ({
        ...prev,
        nearby_highlights: [...prev.nearby_highlights, highlightInput.trim()]
      }));
      setHighlightInput('');
    }
  };

  const removeHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      nearby_highlights: prev.nearby_highlights.filter((_, i) => i !== index)
    }));
  };

  // ========= FILE UPLOAD LOGIC =========

  const uploadFiles = async (files, field, setUploading, limit) => {
    const currentCount = (formData[field] || []).length;
    const remaining = limit - currentCount;
    if (remaining <= 0) {
      toast.error(`Upload limit reached (${limit} max). ${isPremium ? '' : 'Upgrade to premium for up to 20 uploads.'}`);
      return;
    }
    const filesToUpload = files.slice(0, remaining);
    if (filesToUpload.length < files.length) {
      toast.warning(`Only uploading ${filesToUpload.length} of ${files.length} files (limit: ${limit})`);
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of filesToUpload) {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload`, form, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(res.data.url);
      }
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), ...uploaded]
      }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeFloorPlan = (index) => {
    setFormData(prev => ({
      ...prev,
      floor_plan_images: prev.floor_plan_images.filter((_, i) => i !== index)
    }));
  };

  const handleDocUpload = async (files, docName, isMandatory) => {
    if (!files?.length) return;
    setUploadingDocs(prev => ({ ...prev, [docName]: true }));
    try {
      const file = files[0];
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      const newDoc = {
        name: docName,
        url: res.data.url,
        is_mandatory: isMandatory,
        status: 'Uploaded',
        document_type: docName
      };

      setFormData(prev => {
        const filtered = (prev.documents || []).filter(d => d.name !== docName);
        return {
          ...prev,
          documents: [...filtered, newDoc]
        };
      });
      toast.success(`${docName} uploaded`);
    } catch (error) {
      toast.error(`Upload failed for ${docName}`);
    } finally {
      setUploadingDocs(prev => ({ ...prev, [docName]: false }));
    }
  };

  const removeDoc = (docName) => {
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter(d => d.name !== docName)
    }));
  };

  // ========= VENTURE UNIT MANAGEMENT =========

  const addUnit = () => {
    setFormData(prev => ({
      ...prev,
      units: [...prev.units, {
        unit_number: `P-${String(prev.units.length + 1).padStart(3, '0')}`,
        size: '',
        size_unit: 'sqft',
        facing_direction: '',
        price: '',
        price_per_sqft: null,
        status: 'available',
        images: [],
        notes: ''
      }]
    }));
  };

  const updateUnit = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.units];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate price_per_sqft
      if (field === 'price' || field === 'size') {
        const price = parseFloat(field === 'price' ? value : updated[index].price);
        const size = parseFloat(field === 'size' ? value : updated[index].size);
        if (price > 0 && size > 0) {
          updated[index].price_per_sqft = Math.round(price / size);
        }
      }
      return { ...prev, units: updated };
    });
  };

  const removeUnit = (index) => {
    setFormData(prev => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index)
    }));
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) {
          toast.error('CSV must have a header row and at least one data row');
          return;
        }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const newUnits = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const row = {};
          headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
          const size = parseFloat(row.size) || 0;
          const price = parseFloat(row.price) || 0;
          newUnits.push({
            unit_number: row.unit_number || `P-${String(i).padStart(3, '0')}`,
            size: row.size || '',
            size_unit: row.size_unit || 'sqft',
            facing_direction: row.facing_direction || '',
            price: row.price || '',
            price_per_sqft: size > 0 && price > 0 ? Math.round(price / size) : null,
            status: row.status || 'available',
            images: [],
            notes: row.notes || ''
          });
        }
        setFormData(prev => ({
          ...prev,
          units: [...prev.units, ...newUnits]
        }));
        toast.success(`Imported ${newUnits.length} units from CSV`);
      } catch (err) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      latitude: locationData.lat,
      longitude: locationData.lng,
      // Only update address fields if they are empty or if the user dragged the marker (which implies new location)
      location_city: locationData.city || prev.location_city,
      location_state: locationData.state || prev.location_state,
      location_pincode: locationData.pincode || prev.location_pincode,
      full_address: locationData.full_address || prev.full_address,
      location_area: locationData.area || prev.location_area
    }));
  };

  // ========= VENTURE PARTNERS MANAGEMENT =========

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...(prev.partners || []), {
        name: '',
        phone: '',
        email: '',
        role: 'developer',
        rera_number: '',
        company_name: '',
        share_percentage: ''
      }]
    }));
  };

  const updatePartner = (index, field, value) => {
    setFormData(prev => {
      const updated = [...(prev.partners || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, partners: updated };
    });
  };

  const removePartner = (index) => {
    setFormData(prev => ({
      ...prev,
      partners: (prev.partners || []).filter((_, i) => i !== index)
    }));
  };

  // ========= SUBMIT =========

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const price = parseFloat(formData.price);
      const size = parseFloat(formData.size);

      if (isNaN(price) || isNaN(size)) {
        toast.error('Please enter valid numeric values for price and size');
        setLoading(false);
        return;
      }

      const data = {
        ...formData,
        price: price,
        size: size,
        registration_charges: formData.registration_charges && !isNaN(parseFloat(formData.registration_charges))
          ? parseFloat(formData.registration_charges) : null,
        maintenance_charges: formData.maintenance_charges && !isNaN(parseFloat(formData.maintenance_charges))
          ? parseFloat(formData.maintenance_charges) : null,
        price_per_sqft: pricePerSqft,
        // Detailed sanitization for all optional numeric fields to prevent 422 errors
        total_units: (formData.total_units && !isNaN(parseInt(formData.total_units))) ? parseInt(formData.total_units) : null,
        bedrooms: (formData.bedrooms && !isNaN(parseInt(formData.bedrooms))) ? parseInt(formData.bedrooms) : null,
        bathrooms: (formData.bathrooms && !isNaN(parseInt(formData.bathrooms))) ? parseInt(formData.bathrooms) : null,
        floor_number: (formData.floor_number && !isNaN(parseInt(formData.floor_number))) ? parseInt(formData.floor_number) : null,
        total_floors: (formData.total_floors && !isNaN(parseInt(formData.total_floors))) ? parseInt(formData.total_floors) : null,
        master_land_area: (formData.master_land_area && !isNaN(parseFloat(formData.master_land_area)))
          ? parseFloat(formData.master_land_area) : null,
        latitude: (formData.latitude && !isNaN(parseFloat(formData.latitude))) ? parseFloat(formData.latitude) : null,
        longitude: (formData.longitude && !isNaN(parseFloat(formData.longitude))) ? parseFloat(formData.longitude) : null,
        documents: formData.documents || [],
      };



      // Add venture-specific data
      if (formData.property_type === 'venture') {
        data.units = (formData.units || []).map(u => ({
          ...u,
          size: parseFloat(u.size) || 0,
          price: parseFloat(u.price) || 0,
          price_per_sqft: u.price_per_sqft || null,
        }));
        // Clean up partners data - ensure share_percentage is float or null
        data.partners = (formData.partners || []).map(p => ({
          ...p,
          share_percentage: p.share_percentage && !isNaN(parseFloat(p.share_percentage))
            ? parseFloat(p.share_percentage) : null
        }));
      } else {
        // For non-venture, ensure these are null or empty
        data.units = null;
        data.partners = null;
      }

      // Validation: Enforce RERA number if RERA Verified badge is selected
      if (data.badge_rera_verified && !data.rera_number) {
        toast.error('RERA Number is required to mark property as RERA Verified');
        setLoading(false);
        return;
      }

      // Strip video fields for non-premium
      if (!isPremium) {
        data.video_tour_url = '';
        data.virtual_tour_url = '';
      }

      if (isEdit) {
        await axios.put(`${API_URL}/api/properties/${id}`, data, {
          headers: getAuthHeader()
        });
        toast.success('Property updated!');
      } else {
        await axios.post(`${API_URL}/api/properties`, data, {
          headers: getAuthHeader()
        });
        toast.success('Property created!');
      }

      navigate('/dashboard/properties');
    } catch (error) {
      console.error('Submission error details:', error.response?.data);
      if (error.response?.status === 422) {
        console.table(error.response?.data?.detail);
      }
      const message = formatError(error.response?.data?.detail) || 'Failed to save property';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER HELPERS ====================

  const renderSelectField = (label, field, options, placeholder = 'Select...', required = false) => (
    <div className="space-y-2">
      <RequiredLabel htmlFor={field} required={required}>
        {label}
        {!required && <OptionalBadge />}
      </RequiredLabel>
      <Select value={formData[field]} onValueChange={(v) => updateField(field, v)}>
        <SelectTrigger data-testid={`${field}-select`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderSwitchRow = (label, description, field) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <Label htmlFor={field} className="text-sm font-medium">{label}</Label>
        {description && <p className="text-xs text-navy-500">{description}</p>}
      </div>
      <Switch
        id={field}
        checked={!!formData[field]}
        onCheckedChange={(checked) => updateField(field, checked)}
        data-testid={`${field}-switch`}
      />
    </div>
  );

  // ==================== LOADING STATE ====================

  if (fetchingProperty) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="h-[600px] bg-navy-200 rounded-xl" />
        </div>
      </div>
    );
  }

  // ==================== MAIN RENDER ====================

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="property-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard/properties')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">
            {isEdit ? 'Edit Property' : 'Add New Property'}
          </h1>
          <p className="text-navy-600">
            {isEdit ? 'Update property details' : 'Fill in the property details'}
            <span className="text-xs text-red-500 ml-2">* Required fields</span>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ========== SECTION 1: BASIC INFO ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Home className="w-5 h-5 text-sky-500" />
              Basic Information
              <InfoTooltip section="basic" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <RequiredLabel htmlFor="title" required>Property Title</RequiredLabel>
                <Input
                  id="title"
                  placeholder="e.g., Luxurious 3BHK in Bandra West"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  required
                  data-testid="title-input"
                />
              </div>

              {renderSelectField('Property Type', 'property_type', propertyTypes, 'Select type', true)}
              {renderSelectField('Project Status', 'project_status', projectStatuses, 'Select status', true)}

              <div className="space-y-2 md:col-span-2">
                <RequiredLabel htmlFor="description" required>Description</RequiredLabel>
                <Textarea
                  id="description"
                  placeholder="Describe the property in detail — features, nearby attractions, ideal for..."
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="min-h-[120px]"
                  required
                  data-testid="description-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 2: LOCATION DETAILS ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-500" />
              Location Details
              <InfoTooltip section="location" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <RequiredLabel htmlFor="location_state" required>State</RequiredLabel>
                <Select value={formData.location_state} onValueChange={(v) => updateField('location_state', v)}>
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select State" />
                  </SelectTrigger>
                  <SelectContent>
                    {indianStates.map(state => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <RequiredLabel htmlFor="location_city" required>City</RequiredLabel>
                <Input
                  id="location_city"
                  placeholder="e.g., Hyderabad"
                  value={formData.location_city}
                  onChange={(e) => updateField('location_city', e.target.value)}
                  required
                  data-testid="city-input"
                />
              </div>

              <div className="space-y-2">
                <RequiredLabel htmlFor="location_area" required>Area / Locality</RequiredLabel>
                <Input
                  id="location_area"
                  placeholder="e.g., Gachibowli"
                  value={formData.location_area}
                  onChange={(e) => updateField('location_area', e.target.value)}
                  required
                  data-testid="area-input"
                />
              </div>

              <div className="space-y-2">
                <RequiredLabel htmlFor="location_pincode">
                  Pincode
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="location_pincode"
                  placeholder="e.g., 500032"
                  value={formData.location_pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    updateField('location_pincode', val);
                  }}
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  title="Enter a valid 6-digit pincode"
                  data-testid="pincode-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <RequiredLabel htmlFor="full_address">
                  Full Address
                  <OptionalBadge />
                </RequiredLabel>
                <Textarea
                  id="full_address"
                  placeholder="Complete address with landmark..."
                  value={formData.full_address}
                  onChange={(e) => updateField('full_address', e.target.value)}
                  className="min-h-[60px]"
                  data-testid="full-address-input"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label>Pinpoint Location on Map</Label>
                <p className="text-xs text-navy-500 mb-2">
                  Search for your area or drag the marker to the exact property location.
                  Address fields will auto-fill based on the pin.
                </p>
                <LocationPicker
                  initialLat={formData.latitude}
                  initialLng={formData.longitude}
                  onLocationSelect={handleLocationSelect}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 3: PRICING & SIZE ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-sky-500" />
              Pricing & Size
              <InfoTooltip section="pricing" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <RequiredLabel htmlFor="price" required>Price (₹)</RequiredLabel>
                <Input
                  id="price"
                  type="number"
                  placeholder="e.g., 15000000"
                  value={formData.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  required
                  data-testid="price-input"
                />
                {formattedPrice && (
                  <p className="text-sm font-medium text-sky-600" data-testid="formatted-price">
                    {formattedPrice}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <RequiredLabel htmlFor="size" required>Size</RequiredLabel>
                  <Input
                    id="size"
                    type="number"
                    placeholder="e.g., 1500"
                    value={formData.size}
                    onChange={(e) => updateField('size', e.target.value)}
                    required
                    data-testid="size-input"
                  />
                </div>
                <div className="space-y-2">
                  <RequiredLabel htmlFor="size_unit" required>Unit</RequiredLabel>
                  <Select value={formData.size_unit} onValueChange={(v) => updateField('size_unit', v)}>
                    <SelectTrigger data-testid="size-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sizeUnits.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {pricePerSqft && (
                <div className="space-y-2">
                  <Label>Price per {formData.size_unit === 'sqft' ? 'Sq.Ft.' : formData.size_unit}</Label>
                  <div className="h-10 px-3 flex items-center bg-navy-50 border border-navy-200 rounded-md text-navy-700 font-medium" data-testid="price-per-sqft">
                    ₹{pricePerSqft.toLocaleString('en-IN')}/{formData.size_unit}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <RequiredLabel htmlFor="maintenance_charges">
                  Maintenance Charges (₹/month)
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="maintenance_charges"
                  type="number"
                  placeholder="e.g., 5000"
                  value={formData.maintenance_charges}
                  onChange={(e) => updateField('maintenance_charges', e.target.value)}
                  data-testid="maintenance-input"
                />
              </div>

              <div className="space-y-2">
                <RequiredLabel htmlFor="registration_charges">
                  Registration Charges (₹)
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="registration_charges"
                  type="number"
                  placeholder="Optional"
                  value={formData.registration_charges}
                  onChange={(e) => updateField('registration_charges', e.target.value)}
                  data-testid="registration-charges-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 4: PROPERTY DETAILS ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-sky-500" />
              Property Details
              <InfoTooltip section="details" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Apartment/Villa/House fields */}
              {isApartmentOrVilla && (
                <>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="bedrooms" required>Bedrooms</RequiredLabel>
                    <Input
                      id="bedrooms"
                      type="number"
                      placeholder="e.g., 3"
                      value={formData.bedrooms}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        updateField('bedrooms', val);
                      }}
                      min={0} max={99}
                      inputMode="numeric"
                      data-testid="bedrooms-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="bathrooms" required>Bathrooms</RequiredLabel>
                    <Input
                      id="bathrooms"
                      type="number"
                      placeholder="e.g., 2"
                      value={formData.bathrooms}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        updateField('bathrooms', val);
                      }}
                      min={0} max={99}
                      inputMode="numeric"
                      data-testid="bathrooms-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="floor_number">
                      Floor Number
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="floor_number"
                      type="number"
                      placeholder="e.g., 5"
                      value={formData.floor_number}
                      onChange={(e) => updateField('floor_number', e.target.value)}
                      data-testid="floor-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="total_floors">
                      Total Floors
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="total_floors"
                      type="number"
                      placeholder="e.g., 15"
                      value={formData.total_floors}
                      onChange={(e) => updateField('total_floors', e.target.value)}
                      data-testid="total-floors-input"
                    />
                  </div>
                </>
              )}

              {/* Land/Plot fields */}
              {isLandOrPlot && (
                <>
                  {renderSelectField('Plot Facing', 'plot_facing', facingDirections, 'Select Direction')}
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="road_width">
                      Road Width (ft)
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="road_width"
                      placeholder="e.g., 30"
                      value={formData.road_width}
                      onChange={(e) => updateField('road_width', e.target.value)}
                      data-testid="road-width-input"
                    />
                  </div>
                </>
              )}

              {/* Project fields */}
              {isProject && (
                <>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="total_units">
                      Total Units
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="total_units"
                      type="number"
                      placeholder="e.g., 200"
                      value={formData.total_units}
                      onChange={(e) => updateField('total_units', e.target.value)}
                      data-testid="total-units-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="launch_date">
                      Launch Date
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="launch_date"
                      type="date"
                      value={formData.launch_date}
                      onChange={(e) => updateField('launch_date', e.target.value)}
                      data-testid="launch-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="completion_date">
                      Completion Date
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="completion_date"
                      type="date"
                      value={formData.completion_date}
                      onChange={(e) => updateField('completion_date', e.target.value)}
                      data-testid="completion-date-input"
                    />
                  </div>
                </>
              )}

              {/* Venture fields */}
              {isVenture && (
                <>
                  <div className="space-y-2 md:col-span-2 lg:col-span-3">
                    <RequiredLabel htmlFor="venture_name">
                      Venture / Layout Name
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="venture_name"
                      placeholder="e.g., Golden Valley Layout"
                      value={formData.venture_name}
                      onChange={(e) => updateField('venture_name', e.target.value)}
                      data-testid="venture-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="master_land_area">
                      Master Land Area
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="master_land_area"
                      type="number"
                      placeholder="e.g., 25"
                      value={formData.master_land_area}
                      onChange={(e) => updateField('master_land_area', e.target.value)}
                      data-testid="master-land-area-input"
                    />
                  </div>
                  {renderSelectField('Land Area Unit', 'master_land_area_unit', [
                    { value: 'acres', label: 'Acres' },
                    { value: 'hectares', label: 'Hectares' },
                    { value: 'sqft', label: 'Sq. Ft' },
                    { value: 'sqyd', label: 'Sq. Yards' },
                    { value: 'guntas', label: 'Guntas' },
                  ], 'Select Unit')}
                  {renderSelectField('Plot Facing', 'plot_facing', facingDirections, 'Select Direction')}
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="road_width">
                      Road Width (ft)
                      <OptionalBadge />
                    </RequiredLabel>
                    <Input
                      id="road_width"
                      placeholder="e.g., 30"
                      value={formData.road_width}
                      onChange={(e) => updateField('road_width', e.target.value)}
                      data-testid="venture-road-width-input"
                    />
                  </div>
                </>
              )}

              {/* Common fields */}
              {!isLandOrPlot && !isVenture && renderSelectField('Facing Direction', 'facing_direction', facingDirections, 'Select Direction')}
              {renderSelectField('Property Age', 'property_age', propertyAgeOptions, 'Select Age')}

              <div className="space-y-2">
                <RequiredLabel htmlFor="year_built">
                  Year Built
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="year_built"
                  placeholder="e.g., 2022"
                  value={formData.year_built}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    updateField('year_built', val);
                  }}
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  title="Enter a valid 4-digit year"
                  data-testid="year-built-input"
                />
              </div>

              {renderSelectField('Furnishing', 'furnishing_type', furnishingTypes, 'Select Type')}
              {renderSelectField('Parking', 'parking_type', parkingTypes, 'Select Type')}
              {renderSelectField('Water Source', 'water_source', waterSources, 'Select Source')}
            </div>

            <div className="border-t border-navy-100 pt-4 mt-4 space-y-1">
              <p className="text-sm font-medium text-navy-700 mb-2">Additional Features <OptionalBadge /></p>
              {renderSwitchRow('Power Backup', 'Generator / Inverter available', 'power_backup')}
              {renderSwitchRow('Gated Community', 'Within a gated community', 'gated_community')}
              {renderSwitchRow('Corner Plot', 'Property on corner position', 'corner_plot')}
              {isLandOrPlot && renderSwitchRow('Layout Approved', 'Approved layout by authority', 'layout_approved')}
            </div>
          </CardContent>
        </Card>

        {/* ========== VENTURE UNITS MANAGER ========== */}
        {isVenture && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-500" />
                Plot / Unit Management
                <span className="ml-auto text-sm font-normal text-navy-500">
                  {formData.units.length} unit{formData.units.length !== 1 ? 's' : ''}
                  {formData.units.length > 0 && (
                    <> · <span className="text-green-600">{formData.units.filter(u => u.status === 'available').length} available</span>
                      {formData.units.filter(u => u.status === 'sold').length > 0 && (
                        <> · <span className="text-red-600">{formData.units.filter(u => u.status === 'sold').length} sold</span></>
                      )}
                    </>
                  )}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" variant="outline" size="sm" onClick={addUnit} data-testid="add-unit-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Unit
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvUpload}
                    data-testid="csv-upload-input"
                  />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-navy-200 rounded-md hover:bg-navy-50 transition-colors">
                    <Upload className="w-4 h-4" /> Import CSV
                  </span>
                </label>
                <span className="text-xs text-navy-400 ml-2">
                  CSV: unit_number, size, size_unit, facing_direction, price, status
                </span>
              </div>

              {formData.units.length > 0 && (
                <div className="overflow-x-auto border border-navy-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-navy-50 border-b border-navy-200">
                        <th className="text-left px-3 py-2 font-medium text-navy-700 w-8">#</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700">Unit No.</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700">Size</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700 w-20">Unit</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700">Facing</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700">Price (₹)</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700 w-24">₹/sqft</th>
                        <th className="text-left px-3 py-2 font-medium text-navy-700">Status</th>
                        <th className="text-center px-3 py-2 font-medium text-navy-700 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.units.map((unit, index) => (
                        <tr key={index} className={`border-b border-navy-100 hover:bg-navy-50/50 ${unit.status === 'sold' ? 'bg-red-50/30' : unit.status === 'blocked' ? 'bg-amber-50/30' : ''
                          }`}>
                          <td className="px-3 py-2 text-navy-400">{index + 1}</td>
                          <td className="px-3 py-2">
                            <Input
                              value={unit.unit_number}
                              onChange={(e) => updateUnit(index, 'unit_number', e.target.value)}
                              className="h-8 text-sm w-24"
                              placeholder="P-001"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={unit.size}
                              onChange={(e) => updateUnit(index, 'size', e.target.value)}
                              className="h-8 text-sm w-20"
                              placeholder="200"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={unit.size_unit}
                              onChange={(e) => updateUnit(index, 'size_unit', e.target.value)}
                              className="h-8 text-sm border border-navy-200 rounded px-1 bg-white w-full"
                            >
                              <option value="sqft">sqft</option>
                              <option value="sqyd">sqyd</option>
                              <option value="sqmt">sqmt</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={unit.facing_direction}
                              onChange={(e) => updateUnit(index, 'facing_direction', e.target.value)}
                              className="h-8 text-sm border border-navy-200 rounded px-1 bg-white w-full"
                            >
                              <option value="">-</option>
                              <option value="east">East</option>
                              <option value="west">West</option>
                              <option value="north">North</option>
                              <option value="south">South</option>
                              <option value="north_east">NE</option>
                              <option value="north_west">NW</option>
                              <option value="south_east">SE</option>
                              <option value="south_west">SW</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={unit.price}
                              onChange={(e) => updateUnit(index, 'price', e.target.value)}
                              className="h-8 text-sm w-28"
                              placeholder="3500000"
                            />
                          </td>
                          <td className="px-3 py-2 text-navy-500 text-xs">
                            {unit.price_per_sqft ? `₹${unit.price_per_sqft.toLocaleString()}` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={unit.status}
                              onChange={(e) => updateUnit(index, 'status', e.target.value)}
                              className={`h-8 text-xs font-medium border rounded px-2 w-full ${unit.status === 'available' ? 'bg-green-50 text-green-700 border-green-200' :
                                unit.status === 'sold' ? 'bg-red-50 text-red-700 border-red-200' :
                                  'bg-amber-50 text-amber-700 border-amber-200'
                                }`}
                            >
                              <option value="available">Available</option>
                              <option value="sold">Sold</option>
                              <option value="blocked">Blocked</option>
                            </select>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeUnit(index)}
                              className="text-red-400 hover:text-red-600 p-1"
                              title="Remove unit"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {formData.units.length === 0 && (
                <div className="text-center py-8 text-navy-400 border-2 border-dashed border-navy-200 rounded-lg">
                  <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No units added yet. Click "Add Unit" or import from CSV.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== VENTURE PARTNERS ========== */}
        {isVenture && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Venture Partners
                <span className="ml-auto text-sm font-normal text-navy-500">
                  Manage stakeholders & investors
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button type="button" variant="outline" size="sm" onClick={addPartner}>
                <Plus className="w-4 h-4 mr-1" /> Add Partner
              </Button>

              <div className="grid gap-4">
                {(formData.partners || []).map((partner, index) => (
                  <div key={index} className="border border-navy-200 rounded-lg p-4 relative bg-navy-50/30">
                    <button
                      type="button"
                      onClick={() => removePartner(index)}
                      className="absolute top-2 right-2 text-navy-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Partner Name</Label>
                        <Input
                          value={partner.name}
                          onChange={(e) => updatePartner(index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Role</Label>
                        <select
                          value={partner.role}
                          onChange={(e) => updatePartner(index, 'role', e.target.value)}
                          className="flex h-8 w-full rounded-md border border-navy-200 bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-navy-900"
                        >
                          <option value="developer">Developer</option>
                          <option value="landowner">Landowner</option>
                          <option value="investor">Investor</option>
                          <option value="jv_partner">JV Partner</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Company Name</Label>
                        <Input
                          value={partner.company_name}
                          onChange={(e) => updatePartner(index, 'company_name', e.target.value)}
                          placeholder="Company / Entity"
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Phone</Label>
                        <Input
                          value={partner.phone}
                          onChange={(e) => updatePartner(index, 'phone', e.target.value)}
                          placeholder="+91..."
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">RERA Number</Label>
                        <Input
                          value={partner.rera_number}
                          onChange={(e) => updatePartner(index, 'rera_number', e.target.value)}
                          placeholder="If applicable"
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Share %</Label>
                        <Input
                          type="number"
                          value={partner.share_percentage}
                          onChange={(e) => updatePartner(index, 'share_percentage', e.target.value)}
                          placeholder="0-100"
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(formData.partners || []).length === 0 && (
                  <p className="text-sm text-navy-400 text-center py-4 border border-dashed rounded-lg">
                    No partners added yet.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ========== SECTION 5: LEGAL & APPROVALS ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-sky-500" />
              Legal & Approvals
              <OptionalBadge />
              <InfoTooltip section="legal" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <RequiredLabel htmlFor="rera_number">
                  RERA Number
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="rera_number"
                  placeholder="e.g., P02400003403"
                  value={formData.rera_number}
                  onChange={(e) => updateField('rera_number', e.target.value)}
                  disabled={formData.rera_verified}
                  data-testid="rera-number-input"
                  className={formData.rera_verified ? "bg-emerald-50 text-emerald-900 border-emerald-200" : ""}
                />
                {formData.rera_verified && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-emerald-600 font-medium">
                    <ShieldCheck className="w-4 h-4" />
                    RERA ID Verified & Locked
                  </div>
                )}
                {!formData.rera_verified && formData.rera_number && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Verification Pending
                  </p>
                )}
              </div>

              {renderSelectField('Approval Authority', 'approval_authority', approvalAuthorities, 'Select Authority')}
              {renderSelectField('Land Type', 'land_type', landTypes, 'Select Type')}
              {renderSelectField('Ownership Type', 'ownership_type', ownershipTypes, 'Select Type')}

              {isUnderConstruction && (
                <div className="space-y-2">
                  <RequiredLabel htmlFor="possession_date">
                    Expected Possession Date
                    <OptionalBadge />
                  </RequiredLabel>
                  <Input
                    id="possession_date"
                    type="date"
                    value={formData.possession_date}
                    onChange={(e) => updateField('possession_date', e.target.value)}
                    data-testid="possession-date-input"
                  />
                </div>
              )}
            </div>

            {/* ========== DOCUMENT VERIFICATION SECTION ========== */}
            <div className="border-t border-navy-100 pt-6 mt-6 space-y-6">
              <div>
                <Label className="text-base font-bold text-navy-900 mb-2 block">
                  Document Verification
                </Label>
                <p className="text-sm text-navy-500 mb-4">
                  Upload mandatory and optional documents to get your property verified faster.
                </p>

                <div className="space-y-4">
                  {(formData.documents || []).length > 0 ? (
                    (formData.documents || []).map((doc, idx) => (
                      <div key={idx} className="p-4 rounded-xl border border-navy-100 bg-white shadow-sm hover:shadow-md transition-all">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex-1 min-w-[200px]">
                            <div className="flex items-center gap-2">
                              {doc.status === 'Verified' ? (
                                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-navy-200" />
                              )}
                              <span className="font-semibold text-navy-900">{doc.name}</span>
                            </div>
                            <div className="mt-2 flex items-center gap-3">
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-[10px] uppercase">
                                {doc.status}
                              </Badge>
                              <a href={getImageUrl(doc.url)} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline flex items-center gap-1">
                                <Eye className="w-3 h-3" /> View Document
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button type="button" variant="outline" size="sm" onClick={() => removeDoc(doc.name)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 border-2 border-dashed border-navy-100 rounded-xl bg-navy-50/30">
                      <p className="text-sm text-navy-400">No documents uploaded yet.</p>
                    </div>
                  )}
                </div>

                {/* Flexible Document Upload */}
                <div className="mt-6 pt-6 border-t border-navy-50">
                  <div className="bg-sky-50/50 p-4 rounded-xl border border-sky-100">
                    <Label className="text-sm font-bold text-navy-800 mb-3 block">Upload Approval/Certificate</Label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <Select value={customDocName} onValueChange={setCustomDocName}>
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select Document Type (e.g. RERA, HMDA, EC)" />
                          </SelectTrigger>
                          <SelectContent>
                            {commonDocuments.map(doc => (
                              <SelectItem key={doc} value={doc}>{doc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          disabled={!customDocName || uploadingDocs[customDocName]}
                          onChange={(e) => handleDocUpload(e.target.files, customDocName, false)}
                        />
                        <Button type="button" className="w-full sm:w-auto bg-navy-900 hover:bg-navy-800" disabled={!customDocName || uploadingDocs[customDocName]}>
                          {uploadingDocs[customDocName] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          {uploadingDocs[customDocName] ? 'Uploading...' : 'Upload Document'}
                        </Button>
                      </div>
                    </div>
                    <p className="text-[10px] text-navy-500 mt-2">
                      * All documents are optional. Verified documents increase property visibility and trust.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-navy-100 pt-4 mt-2 space-y-1">
              {renderSwitchRow('Encumbrance Certificate', 'EC available for the property', 'encumbrance_certificate')}
              {renderSwitchRow('Bank Approved', 'Property approved by leading banks', 'bank_approved')}
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 6: TRUST BADGES ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-sky-500" />
              Trust Badges
              <OptionalBadge />
              <InfoTooltip section="badges" />
            </CardTitle>
            <p className="text-sm text-navy-500">Toggle badges to display on the property listing</p>
          </CardHeader>
          <CardContent className="space-y-1">
            {/* RERA Verified badge is now automated based on admin approval */}
            {renderSwitchRow('🏦 Bank Approved', 'Approved for home loans', 'badge_bank_approved')}
            {renderSwitchRow('⚖️ Legal Checked', 'Legal documentation verified', 'badge_legal_checked')}
            {renderSwitchRow('🏗 HMDA Approved', 'Hyderabad Metropolitan Development Authority', 'badge_hmda_approved')}
            {renderSwitchRow('📋 DTCP Approved', 'Directorate of Town & Country Planning', 'badge_dtcp_approved')}
          </CardContent>
        </Card>

        {/* ========== SECTION 7: AMENITIES & NEARBY ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Compass className="w-5 h-5 text-sky-500" />
              Amenities & Nearby
              <OptionalBadge />
              <InfoTooltip section="amenities" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Amenities */}
            <div>
              <p className="text-sm font-medium text-navy-700 mb-3">Amenities</p>
              <div className="flex flex-wrap gap-2">
                {defaultAmenities.map(amenity => (
                  <Button
                    key={amenity}
                    type="button"
                    variant={formData.amenities.includes(amenity) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleAmenity(amenity)}
                    className={formData.amenities.includes(amenity) ? 'bg-sky-500 hover:bg-sky-600' : ''}
                    data-testid={`amenity-${amenity.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {amenity}
                  </Button>
                ))}
              </div>
            </div>

            {/* Nearby Facilities */}
            <div>
              <p className="text-sm font-medium text-navy-700 mb-3">Nearby Facilities</p>
              <div className="flex flex-wrap gap-2">
                {nearbyFacilityOptions.map(facility => (
                  <Button
                    key={facility}
                    type="button"
                    variant={formData.nearby_facilities.includes(facility) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleNearbyFacility(facility)}
                    className={formData.nearby_facilities.includes(facility) ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                    data-testid={`nearby-${facility.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {facility}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Highlights */}
            <div>
              <p className="text-sm font-medium text-navy-700 mb-3">Nearby Highlights</p>
              <p className="text-xs text-navy-500 mb-2">Add custom proximity details (e.g., "500m from Metro")</p>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="e.g., 500m from Metro Station"
                  value={highlightInput}
                  onChange={(e) => setHighlightInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                  data-testid="highlight-input"
                />
                <Button type="button" variant="outline" onClick={addHighlight} data-testid="add-highlight-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.nearby_highlights.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.nearby_highlights.map((h, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-navy-100 text-navy-700 text-sm px-3 py-1 rounded-full">
                      {h}
                      <button type="button" onClick={() => removeHighlight(i)} className="text-navy-400 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 8: MEDIA ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="w-5 h-5 text-sky-500" />
              Media
              <InfoTooltip section="media" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Property Images */}
            <div>
              <p className="text-sm font-medium text-navy-700 mb-1">
                Property Images
                <OptionalBadge />
              </p>
              <p className="text-xs text-navy-500 mb-3">Upload photos ({formData.images.length}/{maxImages} used){!isPremium && ' — Upgrade for up to 20'}</p>
              {formData.images.length < maxImages && (
                <FileUploadZone
                  onUpload={(files) => uploadFiles(files, 'images', setUploadingImages, maxImages)}
                  uploading={uploadingImages}
                  label="(max 5MB each)"
                />
              )}
              <ImagePreviewGrid images={formData.images} onRemove={removeImage} />
            </div>

            {/* Floor Plans */}
            <div>
              <p className="text-sm font-medium text-navy-700 mb-1">
                Floor Plan Images
                <OptionalBadge />
              </p>
              <p className="text-xs text-navy-500 mb-3">Upload floor plans ({formData.floor_plan_images.length}/{maxFloorPlans} used)</p>
              {formData.floor_plan_images.length < maxFloorPlans && (
                <FileUploadZone
                  onUpload={(files) => uploadFiles(files, 'floor_plan_images', setUploadingFloorPlans, maxFloorPlans)}
                  uploading={uploadingFloorPlans}
                  label="floor plans"
                />
              )}
              <ImagePreviewGrid images={formData.floor_plan_images} onRemove={removeFloorPlan} />
            </div>

            {/* Video & Virtual Tour — PREMIUM ONLY */}
            <div className={`grid md:grid-cols-2 gap-4 ${!isPremium ? 'relative' : ''}`}>
              {!isPremium && (
                <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur-[2px] rounded-lg flex flex-col items-center justify-center gap-2">
                  <Lock className="w-6 h-6 text-navy-400" />
                  <p className="text-sm font-medium text-navy-700">Premium Feature</p>
                  <p className="text-xs text-navy-500">Upgrade your plan to add video & 360° tours</p>
                </div>
              )}
              <div className="space-y-2">
                <RequiredLabel htmlFor="video_tour_url" >
                  <span className="inline-flex items-center gap-1">
                    <Video className="w-4 h-4" /> Video Tour URL
                    <OptionalBadge />
                  </span>
                </RequiredLabel>
                <Input
                  id="video_tour_url"
                  placeholder="YouTube or video link"
                  value={formData.video_tour_url}
                  onChange={(e) => updateField('video_tour_url', e.target.value)}
                  disabled={!isPremium}
                  data-testid="video-tour-input"
                />
              </div>
              <div className="space-y-2">
                <RequiredLabel htmlFor="virtual_tour_url">
                  <span className="inline-flex items-center gap-1">
                    <Eye className="w-4 h-4" /> 360° Virtual Tour URL
                    <OptionalBadge />
                  </span>
                </RequiredLabel>
                <Input
                  id="virtual_tour_url"
                  placeholder="Virtual tour link"
                  value={formData.virtual_tour_url}
                  onChange={(e) => updateField('virtual_tour_url', e.target.value)}
                  disabled={!isPremium}
                  data-testid="virtual-tour-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========== SECTION 9: SEO & STATUS ========== */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-sky-500" />
              SEO & Status
              <OptionalBadge />
              <InfoTooltip section="seo" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <RequiredLabel htmlFor="meta_title">
                  Meta Title
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="meta_title"
                  placeholder="SEO title (defaults to property title)"
                  value={formData.meta_title}
                  onChange={(e) => updateField('meta_title', e.target.value)}
                  data-testid="meta-title-input"
                />
              </div>

              <div className="space-y-2">
                <RequiredLabel htmlFor="slug">
                  URL Slug
                  <OptionalBadge />
                </RequiredLabel>
                <Input
                  id="slug"
                  placeholder="auto-generated-from-title"
                  value={formData.slug}
                  onChange={(e) => updateField('slug', e.target.value)}
                  data-testid="slug-input"
                />
                <p className="text-xs text-navy-500">Auto-generated from title. Edit to customize.</p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <RequiredLabel htmlFor="meta_description">
                  Meta Description
                  <OptionalBadge />
                </RequiredLabel>
                <Textarea
                  id="meta_description"
                  placeholder="SEO-friendly description for search engines (150-160 chars ideal)"
                  value={formData.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  className="min-h-[60px]"
                  data-testid="meta-description-input"
                />
                {formData.meta_description && (
                  <p className={`text-xs ${formData.meta_description.length > 160 ? 'text-amber-500' : 'text-navy-400'}`}>
                    {formData.meta_description.length}/160 characters
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-navy-100 pt-4 mt-2 space-y-1">
              {renderSwitchRow('⭐ Featured Property', 'Show prominently on your homepage', 'featured')}
            </div>
          </CardContent>
        </Card>

        {/* ========== SUBMIT ========== */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/properties')}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-navy-900 hover:bg-navy-800"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Property' : 'Create Property')}
          </Button>
        </div>
      </form>
    </div>
  );
}
